import { type Static, type StaticDecode, type TSchema, Type } from "typebox";
import * as TypeCompiler from "typebox/compile";
import { Nullable, ReadonlyObject } from "@/shared/schema/schema.ts";
import { Role } from "@/shared/schema/user.ts";
import { type PassedValidator, decoded } from "@/shared/util/decoded.ts";
import { invariant } from "@/shared/util/invariant.ts";

// Must match the monorepo shells' BOOTSTRAP_ELEMENT_ID in rspack/shells.mjs.
const BOOTSTRAP_ELEMENT_ID = "cvp-bootstrap";
const DEPLOY_CONFIG_ELEMENT_ID = "cvp-deploy-config";

const bootstrapSchema = ReadonlyObject({
  logged_in: Type.Boolean(),
  locale: Type.String(),
  default_locale: Type.String(),
  csrf_token: Type.String(),
  required_action: Nullable(Type.String()),
  disable_timeout_and_compatibility_checks: Type.Boolean(),
  current_user_id: Type.Optional(Type.String()),
  current_user_roles: Type.Optional(Type.Array(Role)),
  pusher_key: Type.String(),
  pusher_cluster: Type.String(),
  unleash_context: ReadonlyObject({
    userId: Type.String(),
    properties: ReadonlyObject({
      subdomain: Type.String(),
      companyId: Type.String(),
      cvpAdmin: Type.String(),
      companyCreatedAt: Type.Integer(),
    }),
  }),
  minimum_compatible_version: Type.Integer(),
});

const deployConfigSchema = ReadonlyObject({
  sentryDsn: Type.Optional(Type.String()),
  environment: Type.Optional(Type.String()),
  release: Type.Optional(Type.String()),
  unleashClientKey: Type.Optional(Type.String()),
  awsRegion: Type.Optional(Type.String()),
});

// Read before the deploy config is validated, so that validation has a Sentry
// client to report to.
const sentryConfigSchema = Type.Pick(deployConfigSchema, [
  "sentryDsn",
  "environment",
  "release",
]);

const bootstrapChecker = TypeCompiler.Compile(bootstrapSchema);
const deployConfigChecker = TypeCompiler.Compile(deployConfigSchema);
const sentryConfigChecker = TypeCompiler.Compile(sentryConfigSchema);

export type BootstrapPayload = Static<typeof bootstrapSchema>;
type DeployConfig = Static<typeof deployConfigSchema>;
export type SentryConfig = Static<typeof sentryConfigSchema>;

function readElement(id: string): unknown {
  const element = document.getElementById(id);

  return element?.textContent
    ? (JSON.parse(element.textContent) as unknown)
    : undefined;
}

// For elements the page cannot boot without. Throwing here renders the error
// page rather than leaving a blank one.
export function readJsonScript<T extends TSchema>(
  id: string,
  checker: PassedValidator<T>,
): StaticDecode<T> {
  const raw = readElement(id);

  invariant(
    raw !== undefined,
    `Missing #${id}. It must be rendered before the bundle's script tag.`,
  );

  return checker.Decode(raw);
}

let bootstrap: BootstrapPayload | undefined;

export function getBootstrap(): BootstrapPayload {
  bootstrap ??= readJsonScript(BOOTSTRAP_ELEMENT_ID, bootstrapChecker);

  return bootstrap;
}

// An absent element means no Sentry, not a boot failure.
export function getDeployConfig(): DeployConfig {
  const raw = readElement(DEPLOY_CONFIG_ELEMENT_ID);

  return raw === undefined ? {} : decoded(deployConfigChecker, raw);
}

export function readSentryConfig(): SentryConfig {
  const raw = readElement(DEPLOY_CONFIG_ELEMENT_ID);

  return sentryConfigChecker.Check(raw) ? raw : {};
}
